import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { SWAlertService } from './swalert.service';

@Injectable()
export class SessionExpiredInterceptor implements HttpInterceptor {
  private static isHandlingSessionExpired = false;
  private static isHandlingRequestProblem = false;
  private readonly handlingKey = 'SSC_SESSION_EXPIRED_HANDLING';
  private readonly requestProblemKey = 'SSC_REQUEST_PROBLEM_HANDLING';
  private readonly requestTimeoutMs = 300000;

  constructor(
    private router: Router,
    private swal: SWAlertService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request = next.handle(req);
    const handledRequest = this.isApiRequest(req.url) ? request.pipe(timeout(this.requestTimeoutMs)) : request;

    return handledRequest.pipe(
      catchError((error: any) => {
        if (error.status === 401 && !this.isPublicRequest(req.url)) {
          this.handleSessionExpired();
        }
        else if (this.isRequestTimeout(error) || this.isServerUnavailable(error)) {
          this.handleRequestProblem(error);
        }

        return throwError(error);
      })
    );
  }

  private handleSessionExpired() {
    localStorage.removeItem('UserAcc');
    sessionStorage.removeItem('UserAcc');

    const loading = document.getElementById('divLoading');
    if (loading) {
      loading.style.display = 'none';
    }

    if (SessionExpiredInterceptor.isHandlingSessionExpired || sessionStorage.getItem(this.handlingKey) === 'Y') {
      return;
    }

    SessionExpiredInterceptor.isHandlingSessionExpired = true;
    sessionStorage.setItem(this.handlingKey, 'Y');
    this.swal.SwAlert.Error(this.swal.BoxMsg.Title_Session, '', () => {
      this.router.navigate(['/login']).then(() => {
        setTimeout(() => {
          SessionExpiredInterceptor.isHandlingSessionExpired = false;
          sessionStorage.removeItem(this.handlingKey);
        }, 1000);
      });
    });
  }

  private handleRequestProblem(error: any) {
    this.hideLoading();

    if (SessionExpiredInterceptor.isHandlingRequestProblem || sessionStorage.getItem(this.requestProblemKey) === 'Y') {
      return;
    }

    SessionExpiredInterceptor.isHandlingRequestProblem = true;
    sessionStorage.setItem(this.requestProblemKey, 'Y');

    let message = 'The request is taking longer than expected. Please try again later.';
    if (error && (error.status === 502 || error.status === 503 || error.status === 504)) {
      message = 'The server is temporarily unavailable or the request took too long. Please try again later.';
    }
    else if (error && error.status === 0) {
      message = 'Unable to connect to the server. Please check your connection and try again.';
    }

    this.swal.SwAlert.Error(this.swal.BoxMsg.Title_Error, message, () => {
      SessionExpiredInterceptor.isHandlingRequestProblem = false;
      sessionStorage.removeItem(this.requestProblemKey);
    });
  }

  private hideLoading() {
    const loading = document.getElementById('divLoading');
    if (loading) {
      loading.style.display = 'none';
    }
  }

  private isApiRequest(url: string): boolean {
    return (url || '').toLowerCase().indexOf('api/') >= 0;
  }

  private isRequestTimeout(error: any): boolean {
    return error && error.name === 'TimeoutError';
  }

  private isServerUnavailable(error: any): boolean {
    return error instanceof HttpErrorResponse &&
      (error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504);
  }

  private isPublicRequest(url: string): boolean {
    const path = (url || '').toLowerCase();
    return path.indexOf('api/_system/login') >= 0 ||
      path.indexOf('api/_system/encrypt') >= 0 ||
      path.indexOf('api/_system/decrypt') >= 0 ||
      path.indexOf('api/_system/_forgetpassword') >= 0 ||
      path.indexOf('api/_system/_changepassword') >= 0 ||
      path.indexOf('api/_system/getcbtitle') >= 0 ||
      path.indexOf('api/_system/getname') >= 0 ||
      path.indexOf('api/sessiontimeout/') >= 0;
  }
}
